import { Badge, cn } from "@ibada/ui";
import { getStatusToneClassName } from "@ibada/ui/lib/status-tone";

import { type NotificationPriority } from "@/features/notifications/model/notifications.types";

type NotificationBadgeProps =
  | {
      count?: number;
      priority?: never;
      label?: never;
      className?: string;
    }
  | {
      priority: NotificationPriority;
      label?: string;
      count?: never;
      className?: string;
    };

function formatCount(count: number) {
  return count > 99 ? "99+" : String(count);
}

export function NotificationBadge(props: NotificationBadgeProps) {
  if ("count" in props) {
    const count = props.count ?? 0;
    if (count <= 0) return null;

    const label = formatCount(count);
    const isCompact = label.length === 1;

    return (
      <span
        className={cn(
          // Solid red with bold white digits and a thin ring: the conventional unread count, legible at a glance.
          "pointer-events-none absolute right-0 top-0 z-10 inline-flex translate-x-[30%] -translate-y-[30%] items-center justify-center rounded-full bg-destructive text-[11px] font-bold tabular-nums leading-none text-white ring-2 ring-background",
          isCompact ? "size-[18px]" : "h-[18px] min-w-[22px] px-1.5",
          props.className,
        )}
      >
        {label}
      </span>
    );
  }

  if (props.priority === "NORMAL") return null;

  const variant =
    props.priority === "CRITICAL"
      ? "destructive"
      : props.priority === "HIGH"
        ? "secondary"
        : "secondary";

  const toneKey =
    props.priority === "CRITICAL"
      ? "danger"
      : props.priority === "HIGH"
        ? "warning"
        : "info";

  return (
    <Badge
      variant={variant}
      className={cn(
        "uppercase tracking-[0.08em]",
        variant === "destructive" ? undefined : getStatusToneClassName(toneKey),
        props.className,
      )}
    >
      {props.label ?? props.priority}
    </Badge>
  );
}
